(() => {
  if (window.__parityCommerceHeader) return;
  window.__parityCommerceHeader = true;

  const root = document.documentElement;
  const mobileQuery = window.matchMedia('(max-width: 1022px)');
  const commerceDrawerOrigin = new WeakMap();
  let activeDrawer = null;
  let activeToggle = null;
  let drawerCloseTimer = null;
  const mobilePanelTransitionMs = 300;
  const mobileLayerHideTimers = new WeakMap();
  const mobilePanelEntryAnimations = new WeakMap();

  const closeDesktopPanels = (nav, exceptItem = null) => {
    nav.querySelectorAll('[data-parity-commerce-item].is-open').forEach((item) => {
      if (item === exceptItem) return;

      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-parity-commerce-trigger]');
      const panel = item.querySelector('[data-parity-commerce-panel]');
      trigger?.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  };

  const detachCommerceDrawer = (drawer) => {
    if (!drawer || commerceDrawerOrigin.has(drawer) || !drawer.parentNode || !document.body) return;

    const marker = document.createComment('parity commerce drawer origin');
    drawer.parentNode.insertBefore(marker, drawer);
    commerceDrawerOrigin.set(drawer, { marker });
    document.body.appendChild(drawer);
  };

  const restoreCommerceDrawer = (drawer) => {
    const origin = drawer ? commerceDrawerOrigin.get(drawer) : null;
    if (!drawer || !origin) return;

    if (origin.marker?.parentNode) {
      origin.marker.parentNode.insertBefore(drawer, origin.marker);
      origin.marker.remove();
    }

    commerceDrawerOrigin.delete(drawer);
  };

  const scrollMobileLayerToTop = (layer) => {
    if (!layer) return;

    layer.scrollTop = 0;
    layer.querySelectorAll('[data-parity-commerce-tab-panel]').forEach((tabPanel) => {
      tabPanel.scrollTop = 0;
    });
  };

  const scrollDrawerToTop = (drawer) => {
    drawer?.querySelector?.('.parity-commerce-drawer__surface')?.scrollTo?.(0, 0);
  };

  const setDrawerDrillState = (drawer, isDrilling, backLabel = 'Back to Categories') => {
    if (!drawer) return;

    drawer.classList.toggle('is-drilling', isDrilling);
    const barBack = drawer.querySelector('[data-parity-commerce-bar-back]');
    const rootTitle = drawer.querySelector('[data-parity-commerce-root-title]');
    const barBackLabel = barBack?.querySelector('[data-parity-commerce-back-label]');

    if (barBack) barBack.hidden = !isDrilling;
    if (barBackLabel) barBackLabel.textContent = backLabel;
    if (rootTitle) rootTitle.hidden = isDrilling;
  };

  const alignDesktopPanel = (nav, panel) => {
    const navRect = nav.getBoundingClientRect();
    panel.style.setProperty('--parity-commerce-panel-offset', `${-navRect.left}px`);
  };

  const openDesktopPanel = (item) => {
    const nav = item.closest('[data-parity-commerce-nav]');
    const trigger = item.querySelector('[data-parity-commerce-trigger]');
    const panel = item.querySelector('[data-parity-commerce-panel]');
    if (!nav || !trigger || !panel) return;

    closeDesktopPanels(nav, item);
    alignDesktopPanel(nav, panel);
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
  };

  const shouldOpenDesktopTrigger = (trigger) => {
    if (!trigger || mobileQuery.matches) return false;

    const item = trigger.closest('[data-parity-commerce-item]');
    const panel = item?.querySelector('[data-parity-commerce-panel]');
    if (!item || !panel) return false;

    const isOpen = item.classList.contains('is-open') && panel.hidden === false;
    const pointerEnteredAt = Number(item.dataset.parityCommercePointerEnteredAt || 0);
    return !isOpen || (pointerEnteredAt > 0 && Date.now() - pointerEnteredAt < 100);
  };

  const switchVehicleTab = (tab) => {
    const panelId = tab?.dataset.parityCommerceTab;
    const panel = panelId ? document.getElementById(panelId) : null;
    const tabs = tab?.closest('[data-parity-commerce-vehicle-tabs]');
    const panelSet = panel?.closest('[data-parity-commerce-tab-panels]');
    if (!tab || !panel || !tabs || !panelSet) return false;

    tabs.querySelectorAll('[data-parity-commerce-tab]').forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', String(isActive));
      candidate.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panelSet.querySelectorAll('[data-parity-commerce-tab-panel]').forEach((candidate) => {
      candidate.hidden = candidate !== panel;
    });

    return true;
  };

  const bindDesktopNav = (nav) => {
    if (nav.dataset.parityCommerceBound === 'true') return;
    nav.dataset.parityCommerceBound = 'true';

    nav.querySelectorAll('[data-parity-commerce-item]').forEach((item) => {
      item.addEventListener('pointerenter', () => {
        item.dataset.parityCommercePointerEnteredAt = String(Date.now());
        openDesktopPanel(item);
      });
      item.addEventListener('focusin', () => openDesktopPanel(item));
      item.addEventListener('pointerleave', () => closeDesktopPanels(nav));
    });

    nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const expandedTrigger = nav.querySelector('[data-parity-commerce-trigger][aria-expanded="true"]');
      closeDesktopPanels(nav);
      expandedTrigger?.focus();
    });

    document.addEventListener('pointerdown', (event) => {
      if (nav.contains(event.target)) return;
      closeDesktopPanels(nav);
    });

    window.addEventListener('resize', () => {
      const openPanel = nav.querySelector('[data-parity-commerce-item].is-open [data-parity-commerce-panel]');
      if (openPanel) alignDesktopPanel(nav, openPanel);
    });
  };

  const closeDrawer = ({ immediate = false } = {}) => {
    if (!activeDrawer) return;

    const drawer = activeDrawer;
    const toggle = activeToggle;

    if (drawerCloseTimer) {
      window.clearTimeout(drawerCloseTimer);
      drawerCloseTimer = null;
    }

    suppressLegacyMobileMenus();
    drawer.classList.remove('is-open');
    toggle?.setAttribute('aria-expanded', 'false');
    activeToggle = null;
    activeDrawer = null;

    const finishClose = () => {
      drawer.hidden = true;
      resetMobilePanels(drawer);
      root.classList.remove('parity-commerce-menu-open');
      restoreCommerceDrawer(drawer);
      if (drawerCloseTimer) {
        window.clearTimeout(drawerCloseTimer);
        drawerCloseTimer = null;
      }
    };

    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose();
      return;
    }

    drawerCloseTimer = window.setTimeout(finishClose, 260);
  };

  const openDrawer = (drawer, toggle) => {
    if (!drawer || !mobileQuery.matches) return;

    if (drawerCloseTimer) {
      window.clearTimeout(drawerCloseTimer);
      drawerCloseTimer = null;
    }

    closeDrawer({ immediate: true });
    detachCommerceDrawer(drawer);
    activeDrawer = drawer;
    activeToggle = toggle;
    drawer.hidden = false;
    drawer.classList.remove('is-open');
    setDrawerDrillState(drawer, false);
    root.classList.add('parity-commerce-menu-open');
    toggle?.setAttribute('aria-expanded', 'true');
    const rootPanel = drawer.querySelector('[data-parity-commerce-drawer-root]');
    rootPanel?.removeAttribute('hidden');
    scrollMobileLayerToTop(rootPanel);
    scrollDrawerToTop(drawer);
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
    });
    drawer.querySelector('button[data-parity-commerce-close]')?.focus({ preventScroll: true });
    suppressLegacyMobileMenus();
    window.setTimeout(suppressLegacyMobileMenus, 0);
    window.setTimeout(suppressLegacyMobileMenus, 80);
  };

  const suppressLegacyMobileMenus = () => {
    root.classList.remove('parity-menu-open');
    document.body?.classList.remove('parity-menu-open');

    document.querySelectorAll('.parity-menu-backdrop, .parity-menu-close').forEach((element) => {
      element.remove();
    });

    document.querySelectorAll('.main-menu__content, #main-menu-content').forEach((menu) => {
      menu.classList.remove('is-open', 'is-visible', 'main-menu__content--no-focus', 'parity-menu-active');
      menu.removeAttribute('aria-modal');
    });

    document.querySelectorAll('.parity-commerce-drawer__panel.parity-menu-active').forEach((panel) => {
      panel.classList.remove('parity-menu-active');
      panel.removeAttribute('aria-modal');
      if (panel.getAttribute('role') === 'dialog') panel.removeAttribute('role');
    });

    document.querySelectorAll('.main-nav__item--dropdown.is-open').forEach((item) => {
      item.classList.remove('is-open');
    });

    document.querySelectorAll('.main-nav__toggle-btn[aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  };

  const restoreCommercePanel = (drawer, panel) => {
    if (!drawer || !panel) return;

    const surface = drawer.querySelector('.parity-commerce-drawer__surface');
    if (surface && !surface.contains(panel)) {
      surface.appendChild(panel);
    }

    panel.classList.remove('parity-menu-active');
    panel.removeAttribute('aria-modal');
    if (panel.getAttribute('role') === 'dialog') panel.removeAttribute('role');
    root.classList.remove('parity-menu-open');
    document.body?.classList.remove('parity-menu-open');
  };

  const clearScheduledMobileLayerHide = (layer) => {
    if (!layer) return;

    const timer = mobileLayerHideTimers.get(layer);
    if (!timer) return;

    window.clearTimeout(timer);
    mobileLayerHideTimers.delete(layer);
  };

  const scheduleMobileLayerHide = (layer, shouldHide) => {
    if (!layer) return;

    clearScheduledMobileLayerHide(layer);
    const timer = window.setTimeout(() => {
      mobileLayerHideTimers.delete(layer);
      if (shouldHide && !shouldHide()) return;
      layer.hidden = true;
    }, mobilePanelTransitionMs);
    mobileLayerHideTimers.set(layer, timer);
  };

  const cancelMobilePanelEntry = (panel) => {
    const animation = panel ? mobilePanelEntryAnimations.get(panel) : null;
    if (!animation) return;

    animation.cancel();
    mobilePanelEntryAnimations.delete(panel);
  };

  const animateMobilePanelEntry = (panel) => {
    if (!panel || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (typeof panel.animate !== 'function') return;

    const animation = panel.animate(
      [
        { transform: 'translateX(100%)' },
        { transform: 'translateX(0)' },
      ],
      {
        duration: 240,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'both',
      },
    );

    mobilePanelEntryAnimations.set(panel, animation);
    animation.finished
      .catch(() => {})
      .then(() => {
        if (mobilePanelEntryAnimations.get(panel) !== animation) return;

        mobilePanelEntryAnimations.delete(panel);
        animation.cancel();
      });
  };

  const resetMobilePanels = (drawer = activeDrawer) => {
    document.querySelectorAll('[data-parity-commerce-mobile-panel]').forEach((panel) => {
      clearScheduledMobileLayerHide(panel);
      cancelMobilePanelEntry(panel);
      panel.classList.remove('is-active');
      panel.dataset.parityCommercePanelFrame = 'idle';
      panel.hidden = true;
    });

    const rootPanel = drawer?.querySelector('[data-parity-commerce-drawer-root]')
      || document.querySelector('[data-parity-commerce-drawer-root]');
    clearScheduledMobileLayerHide(rootPanel);
    rootPanel?.removeAttribute('hidden');
    scrollMobileLayerToTop(rootPanel);
    scrollDrawerToTop(drawer);
    setDrawerDrillState(drawer, false);

    document.querySelectorAll('[data-parity-commerce-open-panel][aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.focus({ preventScroll: true });
    });
  };

  const activateMobilePanel = (panel) => {
    cancelMobilePanelEntry(panel);
    scrollMobileLayerToTop(panel);
    panel.dataset.parityCommercePanelFrame = 'start';
    panel.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        panel.dataset.parityCommercePanelFrame = 'active';
        panel.classList.add('is-active');
        animateMobilePanelEntry(panel);
      });
    });
  };

  const showMobilePanel = (drawer, panel, opener = null) => {
    const rootPanel = drawer?.querySelector('[data-parity-commerce-drawer-root]')
      || document.querySelector('[data-parity-commerce-drawer-root]');
    const rootWasVisible = rootPanel && rootPanel.hidden === false;
    clearScheduledMobileLayerHide(rootPanel);

    if (!rootWasVisible) rootPanel?.setAttribute('hidden', '');

    const outgoingPanels = [];

    document.querySelectorAll('[data-parity-commerce-mobile-panel]').forEach((candidate) => {
      clearScheduledMobileLayerHide(candidate);
      if (candidate !== panel) cancelMobilePanelEntry(candidate);
      if (candidate !== panel && candidate.hidden === false) outgoingPanels.push(candidate);
      candidate.classList.remove('is-active');
      candidate.dataset.parityCommercePanelFrame = 'idle';
      if (candidate === panel) {
        candidate.hidden = false;
      }
    });

    document.querySelectorAll('[data-parity-commerce-open-panel]').forEach((button) => {
      button.setAttribute('aria-expanded', button === opener ? 'true' : 'false');
    });

    const backLabel = panel.dataset.parityCommerceBackLabel || 'Back to Categories';
    setDrawerDrillState(drawer, true, backLabel);
    restoreCommercePanel(drawer, panel);
    scrollDrawerToTop(drawer);
    activateMobilePanel(panel);
    if (rootWasVisible) {
      scheduleMobileLayerHide(rootPanel, () => Boolean(drawer?.querySelector('[data-parity-commerce-mobile-panel].is-active:not([hidden])')));
    }
    outgoingPanels.forEach((outgoingPanel) => {
      scheduleMobileLayerHide(outgoingPanel, () => !outgoingPanel.classList.contains('is-active'));
    });
    suppressLegacyMobileMenus();
    window.setTimeout(() => {
      restoreCommercePanel(drawer, panel);
      suppressLegacyMobileMenus();
    }, 0);
    window.setTimeout(() => {
      restoreCommercePanel(drawer, panel);
      suppressLegacyMobileMenus();
    }, 80);

    const barBack = drawer?.querySelector('[data-parity-commerce-bar-back]');
    if (barBack && barBack.hidden === false) {
      barBack.focus({ preventScroll: true });
    } else {
      panel.querySelector('[data-parity-commerce-back]')?.focus({ preventScroll: true });
    }
  };

  const openMobilePanel = (drawer, opener) => {
    const panel = document.getElementById(opener.dataset.parityCommerceOpenPanel);
    if (!panel) return;

    showMobilePanel(drawer, panel, opener);
  };

  const mobilePanelTitle = (panel) => (
    panel?.querySelector?.('.parity-commerce-drawer__panel-title')?.textContent
    || panel?.querySelector?.('[data-parity-commerce-root-title]')?.textContent
    || 'Categories'
  ).replace(/\s+/g, ' ').trim();

  const mobileGroupSubpanelId = (parentPanel, title) => {
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'group';
    return `${parentPanel.id}-${slug}-subpage`;
  };

  const buildMobileGroupPanel = (drawer, summary) => {
    const details = summary?.closest?.('.parity-commerce-drawer__group--nested');
    const parentPanel = summary?.closest?.('[data-parity-commerce-mobile-panel]');
    const surface = drawer?.querySelector?.('.parity-commerce-drawer__surface');
    const groupLink = summary?.querySelector?.('a[href]');
    const sourceList = details?.querySelector?.('.parity-commerce-drawer__links');
    const title = (groupLink?.textContent || summary?.textContent || '').replace(/\s+/g, ' ').replace(/>$/, '').trim();
    if (!details || !parentPanel || !surface || !sourceList || !title) return null;

    const panelId = mobileGroupSubpanelId(parentPanel, title);
    details.dataset.parityCommerceOpenPanel = panelId;
    summary.dataset.parityCommerceOpenPanel = panelId;

    let panel = document.getElementById(panelId);
    if (panel) return panel;

    panel = document.createElement('section');
    panel.className = 'parity-commerce-drawer__panel parity-commerce-drawer__panel--subpage';
    panel.id = panelId;
    panel.hidden = true;
    panel.dataset.parityCommerceMobilePanel = '';
    panel.dataset.parityCommerceParentPanel = parentPanel.id;
    panel.dataset.parityCommerceBackLabel = `Back to ${mobilePanelTitle(parentPanel)}`;

    const allLink = document.createElement('a');
    allLink.className = 'parity-commerce-drawer__subpage-all';
    allLink.href = groupLink?.href || '#';
    allLink.textContent = `All ${title}`;
    panel.appendChild(allLink);

    const list = document.createElement('ul');
    list.className = 'parity-commerce-drawer__subpage-links';
    list.setAttribute('role', 'list');

    sourceList.querySelectorAll('a[href]').forEach((sourceLink) => {
      const label = (sourceLink.textContent || '').replace(/\s+/g, ' ').trim();
      if (!label || /^shop all\b/i.test(label)) return;

      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = sourceLink.href;
      link.textContent = label;
      item.appendChild(link);
      list.appendChild(item);
    });

    panel.appendChild(list);
    surface.appendChild(panel);
    return panel;
  };

  const openMobileGroupPanel = (drawer, summary) => {
    const panel = buildMobileGroupPanel(drawer, summary);
    if (!panel) return false;

    const details = summary.closest('.parity-commerce-drawer__group--nested');
    if (details) details.open = false;
    showMobilePanel(drawer, panel, summary);
    return true;
  };

  const backMobilePanel = (drawer) => {
    const panel = drawer?.querySelector?.('[data-parity-commerce-mobile-panel].is-active:not([hidden])')
      || document.querySelector('[data-parity-commerce-mobile-panel].is-active:not([hidden])');
    const parentPanelId = panel?.dataset.parityCommerceParentPanel;
    const parentPanel = parentPanelId ? document.getElementById(parentPanelId) : null;

    if (parentPanel) {
      showMobilePanel(drawer, parentPanel);
      return true;
    }

    resetMobilePanels(drawer);
    return true;
  };

  const clickGarageAddVehicle = () => {
    const addVehicle = document.querySelector([
      '#cm-mobile-garage .add-vehicle',
      '#cm-mobile-garage .cm_addButton',
      '#cm-garage .add-vehicle',
      '#cm-garage .cm_addButton',
      '.cm_garage .add-vehicle',
      '.cm_garage .cm_addButton',
    ].join(', '));

    if (!addVehicle) return false;

    addVehicle.click();
    return true;
  };

  const handleShopByVehicleAction = (control) => {
    closeDrawer();

    const garageApi = window.ParityConvermaxMakeOrder;
    if (typeof garageApi?.shopByVehicleFromHeader === 'function') {
      try {
        if (garageApi.shopByVehicleFromHeader()) return true;
      } catch (_error) {
        // Fall back to the native garage/button behavior below.
      }
    }

    if (clickGarageAddVehicle()) return true;

    const href = control?.href || control?.getAttribute?.('href');
    if (href) window.location.href = href;
    return true;
  };

  const handleMobileDrawerAction = (event) => {
    const tab = event.target.closest('[data-parity-commerce-tab]');
    const close = event.target.closest('[data-parity-commerce-close]');
    const opener = event.target.closest('[data-parity-commerce-open-panel]');
    const back = event.target.closest('[data-parity-commerce-back]');
    const shopVehicle = event.target.closest('[data-parity-commerce-shop-vehicle]');
    const nestedSummary = event.target.closest('.parity-commerce-drawer__group--nested > summary');
    const control = tab || close || opener || back || shopVehicle || nestedSummary;
    if (!control) return false;

    const drawer = control.closest('[data-parity-commerce-drawer]')
      || activeDrawer
      || document.querySelector('[data-parity-commerce-drawer]');
    if (!drawer && !tab) return false;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (tab) {
      return switchVehicleTab(tab);
    }

    if (close) {
      closeDrawer();
      return true;
    }

    if (opener) {
      openMobilePanel(drawer, opener);
      return true;
    }

    if (nestedSummary) {
      return openMobileGroupPanel(drawer, nestedSummary);
    }

    if (shopVehicle) {
      return handleShopByVehicleAction(shopVehicle);
    }

    return backMobilePanel(drawer);
  };

  const blockUnderlyingDrawerEvent = (event) => {
    if (!activeDrawer || activeDrawer.hidden === true || !mobileQuery.matches) return false;
    if (!root.classList.contains('parity-commerce-menu-open')) return false;
    if (event.target?.closest?.('[data-parity-commerce-drawer]')) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    suppressLegacyMobileMenus();
    return true;
  };

  const findDrawerForToggle = (toggle) => {
    const explicitId = toggle.getAttribute('data-parity-commerce-drawer-id');
    if (explicitId) return document.getElementById(explicitId);

    const header = toggle.closest('header, store-header, .site-header, .cc-header, .shopify-section');
    return header?.querySelector('[data-parity-commerce-drawer]')
      || document.querySelector('[data-parity-commerce-drawer]');
  };

  const bindDrawer = (drawer) => {
    if (drawer.dataset.parityCommerceBound === 'true') return;
    drawer.dataset.parityCommerceBound = 'true';

    drawer.addEventListener('click', (event) => {
      handleMobileDrawerAction(event);
    });
  };

  const bind = () => {
    document.querySelectorAll('[data-parity-commerce-nav]').forEach(bindDesktopNav);
    document.querySelectorAll('[data-parity-commerce-drawer]').forEach(bindDrawer);
    document.querySelectorAll('[data-parity-commerce-menu-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-haspopup', 'dialog');
      if (!toggle.hasAttribute('aria-expanded')) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('click', (event) => {
    if (blockUnderlyingDrawerEvent(event)) return;
    if (handleMobileDrawerAction(event)) return;

    const desktopTrigger = event.target.closest('[data-parity-commerce-trigger]');
    if (shouldOpenDesktopTrigger(desktopTrigger)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openDesktopPanel(desktopTrigger.closest('[data-parity-commerce-item]'));
      return;
    }

    const toggle = event.target.closest('[data-parity-commerce-menu-toggle]');
    if (!toggle || !mobileQuery.matches) return;

    const drawer = findDrawerForToggle(toggle);
    if (!drawer) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openDrawer(drawer, toggle);
  }, true);

  document.addEventListener('pointerdown', (event) => {
    blockUnderlyingDrawerEvent(event);
  }, true);

  document.addEventListener('focusin', (event) => {
    if (!activeDrawer || activeDrawer.hidden === true || !mobileQuery.matches) return;
    if (event.target?.closest?.('[data-parity-commerce-drawer]')) return;

    activeDrawer.querySelector('button[data-parity-commerce-close]')?.focus({ preventScroll: true });
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  mobileQuery.addEventListener?.('change', () => {
    if (!mobileQuery.matches) closeDrawer();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
